import {
  fulfillmentFirestoreId,
  useShippingStatus,
} from "features/purchases/useShippingStatus";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "store";
import useSWRImmutable from "swr/immutable";
import { z } from "zod/mini";

import { FETCH_STATUS } from "./constants/fetchStatus";
import { INITIAL_LIMIT } from "./constants/initialLimit";
import { convertSettlementToCardProps } from "./functions/convertSettlementToCardProps";
import { fetchSettlements } from "./functions/fetchSettlements";
import { filterSettlementsByPeriod } from "./functions/filterSettlementsByPeriod";
import { getShippingLabelStatus } from "./functions/getShippingLabelStatus";

import type { FetchStatus } from "./types/fetchStatus";
import type { ParsedSettlement } from "./types/parsedSettlement";
import type {
  LegacyPortalShippingStatus,
  LegacyPortalShippingStatusDispatchStatus,
} from "@spwn-portal/grpc";
import type { ChangeEventHandler } from "react";

const DocumentReferenceLikeSchema = z.object({ id: z.string() });
const DocumentReferenceLikeArraySchema = z.array(DocumentReferenceLikeSchema);

const extractFulfillmentIds = (settlements: ParsedSettlement[]): string[] => {
  return settlements.flatMap((settlement) => {
    const parseResult = DocumentReferenceLikeArraySchema.safeParse(
      settlement.fulfillmentRefs
    );
    if (!parseResult.success) return [];
    return parseResult.data.map((reference) => reference.id);
  });
};

const getDispatchStatuses = (
  settlement: ParsedSettlement,
  shippingStatusesMap: Map<string, LegacyPortalShippingStatus>
): LegacyPortalShippingStatusDispatchStatus[] => {
  const parseResult = DocumentReferenceLikeArraySchema.safeParse(
    settlement.fulfillmentRefs
  );
  if (!parseResult.success) return [];

  return parseResult.data.reduce<LegacyPortalShippingStatusDispatchStatus[]>(
    (dispatchStatuses, reference) => {
      const parseResult = fulfillmentFirestoreId.safeParse(reference.id);
      if (!parseResult.success) return dispatchStatuses;
      const shippingStatus = shippingStatusesMap.get(parseResult.data);
      if (shippingStatus?.status !== undefined) {
        dispatchStatuses.push(shippingStatus.status);
      }
      return dispatchStatuses;
    },
    []
  );
};

const QUERY_KEY = "year";
const DEFAULT_FILTER = "last3Months";

const YEAR_PATTERN = /^\d{4}$/;

const parseYearParam = (search: string): string => {
  const yearParam = new URLSearchParams(search).get(QUERY_KEY);
  if (yearParam === null || !YEAR_PATTERN.test(yearParam))
    return DEFAULT_FILTER;
  return yearParam;
};

export const useThis = () => {
  const user = useSelector((state) => state.auth.user);
  const history = useHistory();
  const { pathname, search } = useLocation();

  const [isShowingAll, setIsShowingAll] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(() =>
    parseYearParam(search)
  );

  // URL変更（ブラウザバック等）をstateに同期
  useEffect(() => {
    setSelectedFilter(parseYearParam(search));
  }, [search]);

  const {
    data: allSettlements,
    error,
    isLoading,
  } = useSWRImmutable<ParsedSettlement[], Error>(
    user.uid !== "" ? ["settlements", user.uid] : null,
    async () => fetchSettlements(user.uid)
  );

  const filteredSettlements = Array.isArray(allSettlements)
    ? filterSettlementsByPeriod({
        filter: selectedFilter,
        settlements: allSettlements,
      })
    : [];

  const settlements = isShowingAll
    ? filteredSettlements
    : filteredSettlements.slice(0, INITIAL_LIMIT);

  const fulfillmentFirestoreIds = extractFulfillmentIds(settlements);

  const { shippingStatuses } = useShippingStatus(fulfillmentFirestoreIds);

  const fetchStatus: FetchStatus = isLoading
    ? FETCH_STATUS.LOADING
    : error
      ? FETCH_STATUS.ERROR
      : FETCH_STATUS.SUCCESS;

  const handleLoadMore = () => {
    setIsShowingAll(true);
  };

  const handleSelectFilter: ChangeEventHandler<HTMLSelectElement> = (event) => {
    const { value } = event.currentTarget;
    setSelectedFilter(value);
    const queryParameters = new URLSearchParams(search);
    if (value === DEFAULT_FILTER) {
      queryParameters.delete(QUERY_KEY);
    } else {
      queryParameters.set(QUERY_KEY, value);
    }
    const newLocation = [pathname, queryParameters.toString()]
      .filter(Boolean)
      .join("?");
    history.replace(newLocation);
    setIsShowingAll(false);
  };

  const settlementYearList = (() => {
    if (!Array.isArray(allSettlements)) return [];

    const countsMap = new Map<number, number>();

    allSettlements.forEach((order) => {
      if (order.createdAt === null || order.createdAt === undefined) return;

      const date = order.createdAt.toDate();
      const year = date.getFullYear();
      countsMap.set(year, (countsMap.get(year) ?? 0) + 1);
    });

    return Array.from(countsMap.entries())
      .map(([year, numOfSettlements]) => ({ year, numOfSettlements }))
      .sort((a, b) => b.year - a.year);
  })();

  const allHistoryCards = settlements.map((settlement) => {
    const dispatchStatuses = getDispatchStatuses(settlement, shippingStatuses);
    const shippingLabelStatus = getShippingLabelStatus(dispatchStatuses);
    return {
      ...convertSettlementToCardProps(settlement),
      shippingLabelStatus,
    };
  });

  const showLoadMore =
    !isShowingAll && filteredSettlements.length > INITIAL_LIMIT;

  return {
    allHistoryCards,
    fetchStatus,
    handleLoadMore,
    handleSelectFilter,
    selectedFilter,
    settlementYearList,
    showLoadMore,
  };
};
